import {
  AccountType,
  IBreadcrumbBarItem,
  IOption,
  DropdownInput,
} from "component-library";
import { i18n } from "i18n";
import { checkIsAgent, checkIsYp } from "shared-auth";
import { dataTestIds } from "data-testids";
import PageKey from "config/PageKey";
import { IListItem } from "models/common";
import { IJobsPageTranslation } from "models/translation";
import defaultsWithFallback from "helpers/defaultsWithFallback";
import { ListItemTranslator } from "helpers/translationHelpers";
import { fallbackTranslation } from "services/api/fallbackData";
import { jobsTabSegments } from "services/applicationMap";
import {
  FilterType,
  OpenClosedRoles,
  SortingType,
} from "hoc/withJobsFilter/withJobsFilter.config";
import { IJobsPageCommonTexts } from "pages/JobsPage/JobsPage";
import { JobsPageTab } from "pages/JobsPage/JobsPage.config";
import { IAllOpportunitiesPageProps } from "pages/JobsPage/AllOpportunitiesPage/AllOpportunitiesPage";
import { IAllPaidPageProps } from "pages/JobsPage/AllPaidPage/AllPaidPage";
import { IMatchingMyFilterPageProps } from "pages/JobsPage/MatchingMyFilterPage/MatchingMyFilterPage";

export const breadcrumbsKeys = {
  myAccount: "MyAccount",
};

export const INPUT_NAMES = {
  showType: "showType",
  sortingType: "sortingType",
  openClosedRoles: "openClosedRoles",
};

export const accountTypeToFoldersMapping = {
  [AccountType.Performer]: {
    root: "performer",
    tabs: "performerTabs",
    labels: "performerPageHeaderLabels",
    filterOptions: "performerFilterFilterOptions",
    searchPlaceholder: "performerFilterSearch",
  },
  [AccountType.Agent]: {
    root: "agent",
    tabs: "agentTabs",
    labels: "agentPageHeaderLabels",
    filterOptions: "agentFilterFilterOptions",
    searchPlaceholder: "agentFilterSearch",
  },
};

export const urlSegmentToTabMap = {
  [jobsTabSegments.myFilters]: JobsPageTab.MyFilter,
  [jobsTabSegments.allPaid]: JobsPageTab.AllPaid,
  [jobsTabSegments.allOpportunities]: JobsPageTab.All,
  [jobsTabSegments.clientAlerts]: JobsPageTab.ClientAlerts,
  [jobsTabSegments.selfTapeRequests]: JobsPageTab.SelfTapeRequests,
};

const fallbackPageTranslation = fallbackTranslation[PageKey.Jobs];

export function getOpenClosedRolesFilter(
  inputProps:
    | IMatchingMyFilterPageProps
    | IAllPaidPageProps
    | IAllOpportunitiesPageProps,
  handleOpenClosedRoleChange: (option: IOption<OpenClosedRoles>) => void
) {
  const { commonTexts, filterValues } = inputProps;

  return (
    <DropdownInput<string>
      id="filtersOpenClosedRoles"
      name={INPUT_NAMES.openClosedRoles}
      variant="select"
      options={commonTexts.extendedFilterOptions.openClosedRole}
      selected={commonTexts.extendedFilterOptions.openClosedRole.find(
        (option) => option.value === filterValues[FilterType.OpenClosedRoles]
      )}
      value={filterValues[FilterType.OpenClosedRoles]}
      optionSelected={handleOpenClosedRoleChange}
      notTruncateOptionsOnSmall={true}
      data-testid={
        dataTestIds.nuJobsApp["JobsPage.Filters.openClosedRolesDropdown"]
      }
    />
  );
}

export function prepareBreadcrumbs(
  pageTranslation: IJobsPageTranslation,
  accountUrl: string
): IBreadcrumbBarItem[] {
  const listItemTranslator = new ListItemTranslator(
    pageTranslation?.shared?.breadcrumb,
    fallbackPageTranslation.shared.breadcrumb
  );

  return [
    {
      url: accountUrl,
      text: listItemTranslator.get(breadcrumbsKeys.myAccount),
    },
  ];
}

export function getPerformerOpenClosedRolesFilterOptions(): IOption<string>[] {
  return [
    {
      value: OpenClosedRoles.openRoles,
      name: i18n.t("common:jobs.page.openClosedRoles.openRoles"),
    },
    {
      value: OpenClosedRoles.openAndClosedRoles,
      name: i18n.t("common:jobs.page.openClosedRoles.openAndClosedRoles"),
    },
  ];
}

export function prepareTabs(): { id: JobsPageTab; text: string }[] {
  const isAgent = checkIsAgent();
  const isYp = checkIsYp();

  if (isYp)
    return [
      {
        id: JobsPageTab.SelfTapeRequests,
        text: "common:jobs.page.tabs.selfTapeRequests",
      },
    ];

  return [
    {
      id: JobsPageTab.MyFilter,
      text: "common:jobs.page.tabs.matchingMyFilter",
    },
    {
      id: isAgent ? JobsPageTab.All : JobsPageTab.AllPaid,
      text: isAgent
        ? "common:jobs.page.tabs.agent.allOpportunities"
        : "common:jobs.page.tabs.allPaid",
    },
    {
      id: isAgent ? JobsPageTab.ClientAlerts : JobsPageTab.All,
      text: isAgent
        ? "common:jobs.page.tabs.clientAlerts"
        : "common:jobs.page.tabs.performer.allOpportunities",
    },
    {
      id: JobsPageTab.SelfTapeRequests,
      text: "common:jobs.page.tabs.selfTapeRequests",
    },
  ];
}

export function prepareSortingOptions(
  pageTranslation: IJobsPageTranslation
): IOption<string>[] {
  const listItemTranslator = new ListItemTranslator(
    pageTranslation?.shared?.filterSortingOptions,
    fallbackPageTranslation.shared.filterSortingOptions
  );

  return [
    {
      name: listItemTranslator.get(SortingType.SenderNameAsc),
      value: SortingType.SenderNameAsc,
    },
    {
      name: listItemTranslator.get(SortingType.SenderNameDesc),
      value: SortingType.SenderNameDesc,
    },
    {
      name: listItemTranslator.get(SortingType.DateSentDesc),
      value: SortingType.DateSentDesc,
    },
    {
      name: listItemTranslator.get(SortingType.DateSentAsc),
      value: SortingType.DateSentAsc,
    },
    {
      name: listItemTranslator.get(SortingType.ClosesAsc),
      value: SortingType.ClosesAsc,
    },
    {
      name: listItemTranslator.get(SortingType.ClosesDesc),
      value: SortingType.ClosesDesc,
    },
  ];
}

export function prepareFilterOptions(
  pageTranslation: IJobsPageTranslation,
  userType: AccountType
): IOption<string>[] {
  const folderMapping = accountTypeToFoldersMapping[userType];
  const filterOptions: IListItem[] =
    pageTranslation?.[folderMapping.root]?.[folderMapping.filterOptions] ||
    fallbackPageTranslation[folderMapping.root][folderMapping.filterOptions];

  return filterOptions.map((item) => ({
    name: item.Translation,
    value: item.TypeId,
  }));
}

export function prepareTitle(
  pageTranslation: IJobsPageTranslation,
  userType: AccountType,
  tabUrlSegment: string
): string {
  const folderMapping = accountTypeToFoldersMapping[userType];
  const titleListItemTranslator = new ListItemTranslator(
    pageTranslation?.[folderMapping.root]?.[folderMapping.labels],
    fallbackPageTranslation[folderMapping.root][folderMapping.labels]
  );

  return titleListItemTranslator.get(urlSegmentToTabMap[tabUrlSegment]);
}

export function prepareSearchPlaceholder(
  pageTranslation: IJobsPageTranslation,
  userType: AccountType
): string {
  const folderMapping = accountTypeToFoldersMapping[userType];

  return (
    pageTranslation?.[folderMapping.root]?.[folderMapping.searchPlaceholder] ||
    fallbackPageTranslation[folderMapping.root][folderMapping.searchPlaceholder]
  );
}

export function preparePlaceholders(
  pageTranslation: IJobsPageTranslation
): IJobsPageCommonTexts["placeholders"] {
  const translation = defaultsWithFallback(
    pageTranslation.shared,
    fallbackPageTranslation.shared
  );
  const translator = new ListItemTranslator(
    translation.filterFilterDrawer,
    fallbackPageTranslation.shared.filterFilterDrawer
  );

  return {
    productionType: translation.filterProductionTypeFilter,
    contractType: translation.filterContractTypeFilter,
    dateSent: translation.filterDateFilter,
    sender: translator.get("senderCategory"),
    playingAge: translation.playingAge,
    playingAgeFromLabel: translation.playingAgeFromLabel,
    playingAgeFromPlaceholder: translation.playingAgeFromPlaceholder,
    playingAgeToLabel: translation.playingAgeToLabel,
    playingAgeToPlaceholder: translation.playingAgeToPlaceholder,
    playingAgeEmptyLabel: translation.playingAgeEmptyLabel,
    yearLabel: translation.yearLabel,
    yearsLabel: translation.yearsLabel,
    playingAgeOver: pageTranslation.shared.playingAgeOver,
    playingAgeUnder: pageTranslation.shared.playingAgeUnder,
    locationLabel: pageTranslation.shared.locationLabel,
    searchLocationLabel: pageTranslation.shared.searchLocationLabel,
    searchLocationPlaceholder: pageTranslation.shared.searchLocationPlaceholder,
  };
}

export function prepareCommonTexts(
  pageTranslation: IJobsPageTranslation,
  userType: AccountType,
  tabUrlSegment: string
): IJobsPageCommonTexts {
  return {
    title: prepareTitle(pageTranslation, userType, tabUrlSegment),
    searchPlaceholder: prepareSearchPlaceholder(pageTranslation, userType),
    sortingOptions: prepareSortingOptions(pageTranslation),
    extendedFilterOptions: {
      openClosedRole: getPerformerOpenClosedRolesFilterOptions(),
    },
    filterOptions: prepareFilterOptions(pageTranslation, userType),
    placeholders: preparePlaceholders(pageTranslation),
  };
}
